import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FieldTextInput, IconSpinner } from '../../../components';
import classNames from 'classnames';
import * as validators from '../../../util/validators';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearCouponCodeInitialStates,
  verifyCodeSelector,
  verifyCouponCode,
} from '../../../ducks/global.duck';
import css from './SignupForm.module.css';
import Icons from '../../../components/Icons/Icons';
import { useConfiguration } from '../../../context/configurationContext';
import {
  clearVoucherStorage,
  logVoucherDebug,
  normalizeVoucherValue,
  writeVoucherStorage,
} from '../../../util/voucherHelpers';
import { detectCouponApplicability, isAnnualOnlyCoupon } from '../../../util/signupPricing';

function validateSpecialCouponSignup(coupon, email) {
  if (coupon !== 'NNVIV26') {
    return { valid: true, error: null };
  }

  const trimmedEmail = email?.toLowerCase()?.trim();
  if (!trimmedEmail) {
    return { valid: false, error: 'Email is required.' };
  }

  const allowedDomains = ['@nnhellas.gr', '@nnadvisors.gr', '@yopmail.com'];
  const hasValidDomain = allowedDomains.some(domain => trimmedEmail.endsWith(domain));

  if (!hasValidDomain) {
    return {
      valid: false,
      error: 'This coupon is not valid for this domain email.',
    };
  }

  // Skip appliedCoupons check since this is a new user during signup
  return { valid: true, error: null };
}

function RenderVoucherOrId(props) {
  const {
    showPassword,
    formId,
    intl,
    values,
    form,
    isCorporateUser,
    voucherInputId,
    isCustomLandingPage = false,
    // Red plan-mismatch message injected from ProgramSection (computed there)
    couponPlanError,
    voucherSource,
    autoVerifyVoucher = false,
  } = props;

  const dispatch = useDispatch();
  const {
    inProgress,
    success,
    error,
    isCodeValid,
    couponCode,
    successMessage,
    errorMessage,
    coupon,
  } = useSelector(verifyCodeSelector) || {};

  const config = useConfiguration();

  const [clientError, setClientError] = useState(null);

  const autoVerifiedRef = useRef(null);
  const autoVerifyEnabledRef = useRef(autoVerifyVoucher);
  const clearedNonCorporateRef = useRef(false);
  const safeChange = useCallback(
    (field, value) => {
      if (!form || typeof form.change !== 'function') return;
      if (typeof form.getState === 'function' && !form.getState()) return;
      form.change(field, value);
    },
    [form]
  );

  useEffect(() => {
    autoVerifyEnabledRef.current = autoVerifyVoucher;
  }, [autoVerifyVoucher]);

  const onVerifyVoucher = useCallback(async () => {
    const code = normalizeVoucherValue(values?.voucher);
    if (!code) return;

    const email = normalizeVoucherValue(values?.email);

    const validation = validateSpecialCouponSignup(code, email);
    if (!validation.valid) {
      setClientError(validation.error);
      return;
    }

    setClientError(null);
    writeVoucherStorage(code, 'verified');
    await dispatch(verifyCouponCode({ code, email }));
  }, [values?.voucher, values?.email, dispatch]);

  // If user is NOT corporate, ensure any lingering coupon state is cleared
  useEffect(() => {
    if (isCorporateUser) {
      clearedNonCorporateRef.current = false;
      return;
    }

    if (clearedNonCorporateRef.current) return;

    dispatch(clearCouponCodeInitialStates());
    if (values?.voucher === undefined) safeChange('voucher', '');
    clearVoucherStorage();
    autoVerifiedRef.current = null;
    clearedNonCorporateRef.current = true;
  }, [isCorporateUser, dispatch, safeChange, values?.voucher]);

  const getVoucherLabel = () => {
    if (isCustomLandingPage) {
      return intl.formatMessage({ id: 'SignupForm.regularCouponLabel' });
    }
    if (isCorporateUser) {
      return (
        intl.formatMessage({ id: 'SignupForm.corporateCouponLabel' }) || 'Add your employer coupon'
      );
    }
    return (
      intl.formatMessage({ id: 'SignupForm.couponLabel' }) ||
      'Optional: add your coupon or unique code'
    );
  };

  // Clear voucher whenever email changes (prevents coupon being tied to old email)
  const [prevEmail, setPrevEmail] = useState((values?.email || '').trim());

  useEffect(() => {
    const currentEmail = (values?.email || '').trim();

    // skip initial mount
    if (prevEmail === currentEmail) return;

    // email changed -> clear voucher + reset coupon state
    if (values?.voucher) {
      safeChange('voucher', '');
    }
    dispatch(clearCouponCodeInitialStates());
    clearVoucherStorage();
    autoVerifiedRef.current = null;

    setPrevEmail(currentEmail);
  }, [values?.email, prevEmail, values?.voucher, dispatch, safeChange]);

  useEffect(() => {
    if (!autoVerifyVoucher || !autoVerifyEnabledRef.current) return;
    const code = normalizeVoucherValue(values?.voucher);
    if (!code) return;
    if (autoVerifiedRef.current === code) return;
    if (inProgress) return;
    if (success && isCodeValid) return;
    if (!values?.email && code === 'NNVIV26') return;

    autoVerifiedRef.current = code;
    autoVerifyEnabledRef.current = false;
    logVoucherDebug('Auto verifying voucher', { code, source: voucherSource });
    onVerifyVoucher();
  }, [
    autoVerifyVoucher,
    values?.voucher,
    values?.email,
    inProgress,
    success,
    isCodeValid,
    onVerifyVoucher,
    voucherSource,
  ]);

  useEffect(() => {
    if (success && isCodeValid && (couponCode || values?.voucher)) {
      writeVoucherStorage(couponCode || values?.voucher, 'verified');
    }
    if (error && errorMessage) {
      logVoucherDebug('Voucher verify error UI', { errorMessage, code: values?.voucher });
    }
  }, [success, isCodeValid, couponCode, values?.voucher, error, errorMessage]);

  const voucherValidation = isCorporateUser
    ? validators.required(
        intl.formatMessage({ id: 'SignupForm.corporateCouponRequired' }) ||
          'Corporate coupon is required'
      )
    : null;

  const getSuccessMessage = () => {
    if (successMessage) return successMessage;

    const code = couponCode || values?.voucher || 'Coupon';
    const percent = coupon?.percent_off;
    // Force **Annual** for special codes
    const forcedAnnual = isAnnualOnlyCoupon({
      coupon,
      couponCode: code,
      annualCouponCodes: config?.stripeAnnualCoupons,
    });
    const applicable = forcedAnnual ? 'Annual' : detectCouponApplicability(coupon);
    if (applicable === 'Annual') {
      if (percent) return `${code} applied! Enjoy ${percent}% off the Annual Plan`;
      return `${code} applied! Enjoy your discount on the Annual Plan`;
    }
    if (applicable === 'Monthly') {
      if (percent) return `${code} applied! Enjoy ${percent}% off the Monthly Plan`;
      return `${code} applied! Enjoy your discount on the Monthly Plan`;
    }

    // All plans (keep existing/generic behavior)
    if (percent === 100) {
      return (
        intl.formatMessage({ id: 'SignupForm.voucherSuccess100' }) ||
        'Voucher applied! You have a 100% discount.'
      );
    } else if (percent) {
      return (
        intl.formatMessage({ id: 'SignupForm.voucherSuccessPercent' }, { discount: percent }) ||
        `Voucher applied! You have a ${percent}% discount.`
      );
    }
    return (
      intl.formatMessage({ id: 'SignupForm.voucherSuccessGeneral' }) ||
      'Voucher successfully applied!'
    );
  };

  // Show verify button whenever there's text, not in progress, and state is not "success"
  const hasInput = Boolean(values?.voucher && values.voucher.trim().length > 0);
  const showVerifyButton = hasInput && !success && !inProgress;

  // Only disable for corporate users after success
  const shouldDisableInput = Boolean(isCorporateUser && success && isCodeValid);

  // Prefer the plan mismatch error (red) if given
  const showPlanMismatch = Boolean(couponPlanError);

  // NEW: When user types (including clearing), reset coupon state so:
  // - success banner disappears
  // - red error disappears
  // - verify button reappears once there's text
  const handleVoucherChange = e => {
    const val = e.target.value;
    safeChange('voucher', val);
    writeVoucherStorage(val, 'input');
    if (!val || !val.trim()) {
      autoVerifiedRef.current = null;
    }
    autoVerifyEnabledRef.current = false;

    // If there was any prior success/error/mismatch, clear redux state now
    if (success || error || isCodeValid || showPlanMismatch || clientError) {
      dispatch(clearCouponCodeInitialStates());
      setClientError(null);
    }
  };

  return (
    <>
      {success && isCodeValid && (couponCode || values?.voucher) && !showPlanMismatch && (
        <div className={css.successWrapper}>
          <p className={css.success}>
            <Icons name="checkIcon" className={css.checkIcon} /> {getSuccessMessage()}
          </p>
          <button
            type="button"
            className={css.clearButton}
            onClick={() => {
              safeChange('voucher', '');
              dispatch(clearCouponCodeInitialStates());
              setClientError(null);
              clearVoucherStorage();
              autoVerifiedRef.current = null;
            }}
          >
            <Icons name="deleteIcon" />
          </button>
        </div>
      )}

      <div className={classNames(css.formRow, css.fullWidth)}>
        <div className={css.formFld}>
          <div className={css.voucherFld}>
            <FieldTextInput
              className={css.voucherInput}
              type="text"
              id={voucherInputId || (formId ? `${formId}.voucher` : 'voucher')}
              name="voucher"
              placeholder={getVoucherLabel()}
              validate={voucherValidation}
              disabled={shouldDisableInput}
              onBlur={onVerifyVoucher}
              onChange={handleVoucherChange} // <-- reset state on edit/clear
            />
            {showVerifyButton && (
              <span className={css.verifyButton} onClick={onVerifyVoucher}>
                {intl.formatMessage({ id: 'SignupForm.verify' }) || 'Verify'}
              </span>
            )}
            {inProgress && (
              <div className={css.iconSpinner}>
                <IconSpinner />
              </div>
            )}
          </div>

          {/* Red error if coupon doesn't apply to the selected plan */}
          {showPlanMismatch && <p className={css.voucherError}>{couponPlanError}</p>}

          {/* Client-side validation errors */}
          {clientError && hasInput && !showPlanMismatch && (
            <p className={css.voucherError}>{clientError}</p>
          )}

          {/* Backend validation errors */}
          {error && errorMessage && hasInput && !showPlanMismatch && !clientError && (
            <p className={css.voucherError}>
              {errorMessage ||
                intl.formatMessage({ id: 'SignupForm.voucherError' }) ||
                'Invalid voucher code. Please check and try again.'}
            </p>
          )}

          {isCorporateUser && !hasInput && !success && !showPlanMismatch && (
            <p className={css.voucherNote}>
              {intl.formatMessage({ id: 'SignupForm.corporateCouponNote' }) ||
                'A valid corporate coupon is required for registration'}
            </p>
          )}
        </div>
      </div>
    </>
  );
}

export default RenderVoucherOrId;
